import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { BasketPostService } from './basket-post.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-basket-post',
    templateUrl: './basket-post.component.html',
    styleUrls: ['./basket-post.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BasketPostComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = true;
  crudLoading = false;
  showFormLoader = false;

  // Component specific properties
  modalTitle = "Basket Post 960 Map";
  basketPost: any[] = [];
  basketObject = {
    ID: 1,
    BASKET_NAME: "",
    DM_FI_STAT: "",
    POST_BASKET_NAME: ""
  };

  // Data properties
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  gridData: any = null;
  syncSaveDtls: any[] = [];

  // Entity data properties
  entityList: any[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';

  constructor(
    private _snackBar: MatSnackBar,
    private basketPostService: BasketPostService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    const gd = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = gd;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
        this.processGridData();
      }
    } catch (e) {
      console.error('Error in BasketPost init:', e);
    }
  }

  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID || '';
    this.cdr_no = this.rowData.MARS || '';
    this.leType = this.rowData.LE_TYPE || '';

    // 🔴 MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR || '' },
      { key: "LEID", value: this.rowData.LEID || '' },
      { key: "LE Name", value: this.rowData.LE_NAME || '' }
    ];
  }

  private processGridData() {
    if (this.gridData != null) {
      this.syncSaveDtls = [];

      if (this.gridData !== undefined) {
        for (let key in this.gridData) {
          let gridObj: any = {};
          gridObj["combination_key"] = this.gridData[key].data.COMBINATION_KEY;
          this.syncSaveDtls.push(gridObj);
        }
      }
    }
  }

  addNew() {
    this.isSaveClicked = false;
    this.basketPost.push({
      ID: this.basketPost.length,
      BASKET_NAME: "",
      DM_FI_STAT: "",
      POST_BASKET_NAME: ""
    });
  }

  deleteRow(index: number) {
    this.basketPost.splice(index, 1);
    this.isSaveClicked = this.basketPost.length > 0 ? false : true;
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const clob_settings = [{
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      screen_key: this.globalService?.screen_key || ''
    }];

    if(this.basketPost.length > 0)
      {
    this.basketPostService.saveBasket(
      this.GENERAL_CONFIG.base_url,
      this.basketPost,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess == '1') {
          this._snackBar.open('Data added successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  reset() {
    this.basketPost = [];
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
